/**
 * External dependencies
 */
import clsx from 'clsx';

/**
 * WordPress dependencies
 */
import { _x } from '@safe-wordpress/i18n';
import {
	VStack,
	BaseControl,
	ColorPalette,
	GradientPicker,
	type ColorObject,
	type PaletteObject,
	type GradientPickerComponentProps,
} from '@safe-wordpress/components';

/**
 * Internal dependencies
 */
import { Tabs } from '../../../components/tabs';

const colorsAndGradientKeys = [
	'colors',
	'disableCustomColors',
	'gradients',
	'disableCustomGradients',
];

const TAB_IDS = { color: 'color', gradient: 'gradient' };

type HeadingSize = 1 | 2 | 3 | 4 | 5 | 6 | '1' | '2' | '3' | '4' | '5' | '6';

type ColorGradientControlInnerProps = {
	readonly colors?: ColorObject[] | PaletteObject[];
	readonly gradients?: GradientPickerComponentProps[ 'gradients' ];
	readonly disableCustomColors?: boolean;
	readonly disableCustomGradients?: boolean;
	readonly __experimentalIsRenderedInSidebar: boolean;
	readonly className?: string;
	readonly label?: string;
	readonly onColorChange?: ( newColor?: string, index?: number ) => void;
	readonly onGradientChange?: ( gradient?: string ) => void;
	readonly colorValue: string | undefined;
	readonly gradientValue: string | undefined;
	readonly clearable?: boolean;
	readonly showTitle?: boolean;
	readonly enableAlpha?: boolean;
	readonly headingLevel?: HeadingSize;
};
function ColorGradientControlInner( {
	colors,
	gradients,
	disableCustomColors,
	disableCustomGradients,
	__experimentalIsRenderedInSidebar,
	className,
	label,
	onColorChange,
	onGradientChange,
	colorValue,
	gradientValue,
	clearable,
	showTitle = true,
	enableAlpha,
	headingLevel,
}: ColorGradientControlInnerProps ) {
	const canChooseAColor =
		!! onColorChange &&
		( ( colors && colors.length > 0 ) || ! disableCustomColors );
	const canChooseAGradient =
		!! onGradientChange &&
		( ( gradients && gradients.length > 0 ) || ! disableCustomGradients );

	if ( ! canChooseAColor && ! canChooseAGradient ) {
		return null;
	}

	const tabPanels = {
		[ TAB_IDS.color ]: (
			<ColorPalette
				value={ colorValue }
				onChange={
					canChooseAGradient
						? ( newColor ) => {
								if ( onColorChange ) {
									onColorChange( newColor );
								}
								if ( onGradientChange ) {
									onGradientChange();
								}
						  }
						: ( onColorChange as (
								newColor?: string,
								index?: number
						  ) => void )
				}
				{ ...{ colors, disableCustomColors } }
				__experimentalIsRenderedInSidebar={
					__experimentalIsRenderedInSidebar
				}
				clearable={ clearable }
				enableAlpha={ enableAlpha }
				headingLevel={ headingLevel }
			/>
		),
		[ TAB_IDS.gradient ]: (
			<GradientPicker
				value={ gradientValue }
				onChange={
					canChooseAColor
						? ( newGradient ) => {
								if ( onGradientChange ) {
									onGradientChange( newGradient );
								}
								if ( onColorChange ) {
									onColorChange();
								}
						  }
						: ( onGradientChange as ( gradient?: string ) => void )
				}
				{ ...{ gradients, disableCustomGradients } }
				__experimentalIsRenderedInSidebar={
					__experimentalIsRenderedInSidebar
				}
				clearable={ clearable }
				headingLevel={ headingLevel }
			/>
		),
	};

	const renderPanelType = ( type: string ) => (
		<div className="block-editor-color-gradient-control__panel">
			{ tabPanels[ type ] }
		</div>
	);

	return (
		<BaseControl
			__nextHasNoMarginBottom
			className={ clsx(
				'block-editor-color-gradient-control',
				className
			) }
		>
			<fieldset className="block-editor-color-gradient-control__fieldset">
				<VStack spacing={ 1 }>
					{ showTitle && (
						<legend>
							<div className="block-editor-color-gradient-control__color-indicator">
								<BaseControl.VisualLabel>
									{ label }
								</BaseControl.VisualLabel>
							</div>
						</legend>
					) }
					{ canChooseAColor && canChooseAGradient && (
						<div>
							<Tabs
								defaultTabId={
									gradientValue
										? TAB_IDS.gradient
										: !! canChooseAColor && TAB_IDS.color
								}
							>
								<Tabs.TabList>
									<Tabs.Tab tabId={ TAB_IDS.color }>
										{ _x(
											'Color',
											'text',
											'nelio-ab-testing'
										) }
									</Tabs.Tab>
									<Tabs.Tab tabId={ TAB_IDS.gradient }>
										{ _x(
											'Gradient',
											'text',
											'nelio-ab-testing'
										) }
									</Tabs.Tab>
								</Tabs.TabList>
								<Tabs.TabPanel
									tabId={ TAB_IDS.color }
									className="block-editor-color-gradient-control__panel"
									focusable={ false }
								>
									{ tabPanels.color }
								</Tabs.TabPanel>
								<Tabs.TabPanel
									tabId={ TAB_IDS.gradient }
									className="block-editor-color-gradient-control__panel"
									focusable={ false }
								>
									{ tabPanels.gradient }
								</Tabs.TabPanel>
							</Tabs>
						</div>
					) }

					{ ! canChooseAGradient && renderPanelType( TAB_IDS.color ) }
					{ ! canChooseAColor && renderPanelType( TAB_IDS.gradient ) }
				</VStack>
			</fieldset>
		</BaseControl>
	);
}

function ColorGradientControlSelect(
	props: ColorGradientControlInnerProps
): JSX.Element {
	return <ColorGradientControlInner { ...props } />;
}

function ColorGradientControl(
	props: ColorGradientControlInnerProps
): JSX.Element {
	if (
		colorsAndGradientKeys.every( ( key ) => props.hasOwnProperty( key ) )
	) {
		return <ColorGradientControlInner { ...props } />;
	}
	return <ColorGradientControlSelect { ...props } />;
}

export default ColorGradientControl;
